import { useState } from 'react';
import { color } from '../styles/theme';
import { useDeleteStudy, type Study } from '../api/studies';
import { Modal, Button, ErrorNote, Field } from '../components/ui';
import { inputStyle } from '../components/ui';

/**
 * Deleting a study, behind a typed confirmation.
 *
 * A study is the thing every participant record points at, so this is the one
 * destructive action on the screen that cannot be undone from the UI. Typing
 * the name is the guard: it makes the confirmation impossible to click through
 * on muscle memory, and it makes you look at *which* study you picked — the
 * mistake this prevents is not "meant to keep it", it is "meant to delete the
 * other one".
 *
 * The API refuses a study any submission references (409). That is a better
 * answer than a cascade, and the message it returns says what to do instead,
 * so it is shown verbatim.
 */
export function DeleteStudy({
  study,
  onClose,
  onDeleted,
}: {
  study: Study;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const del = useDeleteStudy();
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);

  const name = study.label || 'Untitled study';
  // Forgiving about case and stray spaces, strict about the words.
  const matches = typed.trim().toLowerCase() === name.trim().toLowerCase();

  async function confirm() {
    setError(null);
    try {
      await del.mutateAsync(study.slug);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete that study.');
    }
  }

  return (
    <Modal
      label="Delete study"
      width={520}
      onClose={onClose}
      header={
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            padding: '5px 13px',
            borderRadius: 999,
            background: '#f8e3dd',
            color: '#a4432f',
          }}
        >
          Delete study
        </span>
      }
      footer={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Button
            variant="danger"
            disabled={!matches || del.isPending}
            onClick={() => void confirm()}
            title={matches ? undefined : 'Type the study name to enable this'}
          >
            {del.isPending ? 'Deleting…' : 'Delete this study'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ fontSize: 14, lineHeight: '22px', color: color.ink, margin: 0 }}>
          This removes <strong>{name}</strong> from the registry and from the public site. It cannot
          be undone from here.
        </p>

        <p
          style={{
            fontSize: 12.5,
            lineHeight: '20px',
            color: '#7a4512',
            background: '#f9e8d6',
            border: '1px solid #ecc9a2',
            borderRadius: 12,
            padding: '11px 14px',
            margin: 0,
          }}
        >
          If anyone has applied to this study, the delete will be refused — their records point at
          it. <strong>Unpublish it instead</strong> to take it off the site while keeping the
          history.
        </p>

        <Field label={`Type the study name to confirm`}>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={name}
            autoFocus
            aria-label="Study name confirmation"
            style={inputStyle}
          />
        </Field>

        {error && <ErrorNote message={error} />}
      </div>
    </Modal>
  );
}
